function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import cx from 'clsx';
import _ from 'lodash';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useFloating, offset as offsetMiddleware, flip, shift, autoUpdate } from '@floating-ui/react-dom';
import shallowEqual from 'shallowequal';
import { childrenUtils, createHTMLDivision, customPropTypes, getComponentType, getUnhandledProps, makeDebugger, SUI, useIsomorphicLayoutEffect, getKeyOnly, getKeyOrValueAndKey, useMergedRefs, usePrevious } from '../lib';
import Portal from '../Portal';
import { placementMapping, positions, positionsMapping } from './lib/positions';
import PopupContent from './PopupContent';
import PopupHeader from './PopupHeader';
var debug = makeDebugger('popup');

/**
 * Calculates props specific for Portal component.
 *
 * @param {Object} props
 */
function getPortalProps(props) {
  var _props$on;
  var portalProps = {};
  var on = (_props$on = props.on) !== null && _props$on !== void 0 ? _props$on : ['click', 'hover'];
  var normalizedOn = _.isArray(on) ? on : [on];
  if (props.hoverable) {
    portalProps.closeOnPortalMouseLeave = true;
    portalProps.mouseLeaveDelay = 300;
  }
  if (_.includes(normalizedOn, 'hover')) {
    portalProps.openOnTriggerClick = false;
    portalProps.closeOnTriggerClick = false;
    portalProps.openOnTriggerMouseEnter = true;
    portalProps.closeOnTriggerMouseLeave = true;
    // Taken from SUI: https://git.io/vPmCm
    portalProps.mouseLeaveDelay = 70;
    portalProps.mouseEnterDelay = 50;
  }
  if (_.includes(normalizedOn, 'click')) {
    portalProps.openOnTriggerClick = true;
    portalProps.closeOnTriggerClick = true;
    portalProps.closeOnDocumentClick = true;
  }
  if (_.includes(normalizedOn, 'focus')) {
    portalProps.openOnTriggerFocus = true;
    portalProps.closeOnTriggerBlur = true;
  }
  return portalProps;
}

/**
 * Splits props for Portal & Popup.
 *
 * @param {Object} unhandledProps
 * @param {Boolean} disabled
 */
function partitionPortalProps(unhandledProps, disabled) {
  if (disabled) {
    return {};
  }
  var contentRestProps = _.reduce(unhandledProps, function (acc, val, key) {
    if (!_.includes(Portal.handledProps, key)) acc[key] = val;
    return acc;
  }, {});
  var portalRestProps = _.pick(unhandledProps, Portal.handledProps);
  return {
    contentRestProps: contentRestProps,
    portalRestProps: portalRestProps
  };
}

/**
 * Performs updates when "popperDependencies" are not shallow equal.
 *
 * @param {Array} popperDependencies
 * @param {React.Ref} positionUpdate
 */
function usePositioningEffect(popperDependencies, positionUpdate) {
  var previousDependencies = usePrevious(popperDependencies);
  useIsomorphicLayoutEffect(function () {
    if (positionUpdate.current) {
      positionUpdate.current();
    }
  }, [shallowEqual(previousDependencies, popperDependencies)]);
}

/**
 * A Popup displays additional information on top of a page.
 */
var Popup = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var basic = props.basic,
    className = props.className,
    content = props.content,
    context = props.context,
    children = props.children,
    _props$disabled = props.disabled,
    disabled = _props$disabled === void 0 ? false : _props$disabled,
    _props$eventsEnabled = props.eventsEnabled,
    eventsEnabled = _props$eventsEnabled === void 0 ? true : _props$eventsEnabled,
    flowing = props.flowing,
    header = props.header,
    _props$hideOnScroll = props.hideOnScroll,
    hideOnScroll = _props$hideOnScroll === void 0 ? false : _props$hideOnScroll,
    inverted = props.inverted,
    offset = props.offset,
    _props$pinned = props.pinned,
    pinned = _props$pinned === void 0 ? false : _props$pinned,
    popper = props.popper,
    popperDependencies = props.popperDependencies,
    _props$popperModifier = props.popperModifiers,
    popperModifiers = _props$popperModifier === void 0 ? [] : _props$popperModifier,
    _props$position = props.position,
    position = _props$position === void 0 ? 'top left' : _props$position,
    positionFixed = props.positionFixed,
    size = props.size,
    style = props.style,
    trigger = props.trigger,
    wide = props.wide;
  var unhandledProps = getUnhandledProps(Popup, props);
  var _partitionPortalProps = partitionPortalProps(unhandledProps, disabled),
    contentRestProps = _partitionPortalProps.contentRestProps,
    portalRestProps = _partitionPortalProps.portalRestProps;
  var elementRef = useMergedRefs(ref);
  var positionUpdate = React.useRef();
  var triggerRef = React.useRef();
  var zIndexWasSynced = React.useRef(false);
  var middleware = React.useMemo(function () {
    var mw = [];
    if (offset) {
      var offsetValue = typeof offset === 'function' ? offset : {
        mainAxis: offset[1] || 0,
        crossAxis: offset[0] || 0
      };
      mw.push(offsetMiddleware(offsetValue));
    }
    if (!pinned) {
      mw.push(flip());
    }
    mw.push(shift());
    return mw;
  }, [offset, pinned]);
  var virtualReference = React.useMemo(function () {
    var refSource = _.isNil(context) ? triggerRef : context;
    return {
      getBoundingClientRect: function getBoundingClientRect() {
        var el = (refSource === null || refSource === void 0 ? void 0 : refSource.current) || refSource;
        if (el && el.getBoundingClientRect) {
          return el.getBoundingClientRect();
        }
        return {
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          width: 0,
          height: 0,
          x: 0,
          y: 0
        };
      },
      get contextElement() {
        var el = (refSource === null || refSource === void 0 ? void 0 : refSource.current) || refSource;
        return el || undefined;
      }
    };
  }, [context]);
  var _useFloating = useFloating({
      placement: positionsMapping[position],
      strategy: positionFixed ? 'fixed' : 'absolute',
      middleware: middleware,
      whileElementsMounted: eventsEnabled ? autoUpdate : undefined
    }),
    refs = _useFloating.refs,
    floatingStyles = _useFloating.floatingStyles,
    computedPlacement = _useFloating.placement,
    update = _useFloating.update;
  React.useEffect(function () {
    refs.setReference(virtualReference);
  }, [virtualReference, refs]);
  React.useEffect(function () {
    positionUpdate.current = update;
  }, [update]);

  // ----------------------------------------
  // Effects
  // ----------------------------------------

  usePositioningEffect(popperDependencies, positionUpdate);

  // ----------------------------------------
  // Handlers
  // ----------------------------------------

  var handleClose = function handleClose(e) {
    debug('handleClose()');
    _.invoke(props, 'onClose', e, _objectSpread(_objectSpread({}, props), {}, {
      open: false
    }));
  };
  var handleOpen = function handleOpen(e) {
    debug('handleOpen()');
    _.invoke(props, 'onOpen', e, _objectSpread(_objectSpread({}, props), {}, {
      open: true
    }));
  };
  var handlePortalMount = function handlePortalMount(e) {
    debug('handlePortalMount()');
    _.invoke(props, 'onMount', e, props);
  };
  var handlePortalUnmount = function handlePortalUnmount(e) {
    debug('handlePortalUnmount()');
    positionUpdate.current = null;
    _.invoke(props, 'onUnmount', e, props);
  };

  // ----------------------------------------
  // Render
  // ----------------------------------------

  var renderBody = function renderBody() {
    var classes = cx('ui', placementMapping[computedPlacement], size, getKeyOrValueAndKey(wide, 'wide'), getKeyOnly(basic, 'basic'), getKeyOnly(flowing, 'flowing'), getKeyOnly(inverted, 'inverted'), 'popup transition visible', className);
    var ElementType = getComponentType(props);
    var styles = _objectSpread({
      // Heads up! We need default styles to get working correctly `flowing`
      left: 'auto',
      right: 'auto',
      // This is required to be properly positioned inside wrapping `div`
      position: 'initial'
    }, style);
    var innerElement = /*#__PURE__*/React.createElement(ElementType, _extends({}, contentRestProps, {
      className: classes,
      style: styles,
      ref: elementRef
    }), childrenUtils.isNil(children) ? /*#__PURE__*/React.createElement(React.Fragment, null, PopupHeader.create(header, {
      autoGenerateKey: false
    }), PopupContent.create(content, {
      autoGenerateKey: false
    })) : children);
    var popperRef = function popperRef(node) {
      refs.setFloating(node);
      if (node && !zIndexWasSynced.current) {
        var _popper$style;
        var definedZIndex = popper === null || popper === void 0 || (_popper$style = popper.style) === null || _popper$style === void 0 ? void 0 : _popper$style.zIndex;
        if (_.isUndefined(definedZIndex) && node.firstChild) {
          node.style.zIndex = window.getComputedStyle(node.firstChild).zIndex;
        }
        zIndexWasSynced.current = true;
      }
    };
    return createHTMLDivision(popper || {}, {
      overrideProps: {
        children: innerElement,
        ref: popperRef,
        style: _objectSpread({
          // Fixes layout for floated elements
          display: 'flex'
        }, floatingStyles)
      }
    });
  };
  if (disabled) {
    return trigger;
  }
  var mergedPortalProps = _objectSpread(_objectSpread({}, getPortalProps(props)), portalRestProps);
  debug('portal props:', mergedPortalProps);
  var popupBody = renderBody();
  return /*#__PURE__*/React.createElement(Portal, _extends({}, mergedPortalProps, {
    onClose: handleClose,
    onMount: handlePortalMount,
    onOpen: handleOpen,
    onUnmount: handlePortalUnmount,
    trigger: trigger,
    triggerRef: triggerRef,
    hideOnScroll: hideOnScroll
  }), popupBody);
});
Popup.displayName = 'Popup';
Popup.propTypes = {
  /** An element type to render as (string or function). */
  as: PropTypes.elementType,
  /** Display the popup without the pointing arrow. */
  basic: PropTypes.bool,
  /** Primary content. */
  children: PropTypes.node,
  /** Additional classes. */
  className: PropTypes.string,
  /** Simple text content for the popover. */
  content: customPropTypes.itemShorthand,
  /** Existing element the pop-up should be bound to. */
  context: PropTypes.oneOfType([PropTypes.object, customPropTypes.refObject]),
  /** A disabled popup only renders its trigger. */
  disabled: PropTypes.bool,
  /** Enables the Popper.js event listeners. */
  eventsEnabled: PropTypes.bool,
  /** A flowing Popup has no maximum width and continues to flow to fit its content. */
  flowing: PropTypes.bool,
  /** Takes up the entire width of its offset container. */
  // TODO: implement the Popup fluid layout
  // fluid: PropTypes.bool,

  /** Header displayed above the content in bold. */
  header: customPropTypes.itemShorthand,
  /** Hide the Popup when scrolling the window. */
  hideOnScroll: PropTypes.bool,
  /** Whether the popup should not close on hover. */
  hoverable: PropTypes.bool,
  /** Invert the colors of the Popup. */
  inverted: PropTypes.bool,
  /**
   * Offset values in px unit to apply to rendered popup. The basic offset accepts an
   * array with two numbers in the form [skidding, distance]:
   * - `skidding` displaces the Popup along the reference element
   * - `distance` displaces the Popup away from, or toward, the reference element in the direction of its placement. A positive number displaces it further away, while a negative number lets it overlap the reference.
   *
   * @see https://popper.js.org/docs/v2/modifiers/offset/
   */
  offset: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.number)]),
  /** Events triggering the popup. */
  on: PropTypes.oneOfType([PropTypes.oneOf(['hover', 'click', 'focus']), PropTypes.arrayOf(PropTypes.oneOf(['hover', 'click', 'focus']))]),
  /**
   * Called when a close event happens.
   *
   * @param {SyntheticEvent} event - React's original SyntheticEvent.
   * @param {object} data - All props.
   */
  onClose: PropTypes.func,
  /**
   * Called when the portal is mounted on the DOM.
   *
   * @param {null}
   * @param {object} data - All props.
   */
  onMount: PropTypes.func,
  /**
   * Called when an open event happens.
   *
   * @param {SyntheticEvent} event - React's original SyntheticEvent.
   * @param {object} data - All props.
   */
  onOpen: PropTypes.func,
  /**
   * Called when the portal is unmounted from the DOM.
   *
   * @param {null}
   * @param {object} data - All props.
   */
  onUnmount: PropTypes.func,
  /** Disables automatic repositioning of the component, it will always be placed according to the position value. */
  pinned: PropTypes.bool,
  /** Position for the popover. */
  position: PropTypes.oneOf(positions),
  /** Tells `Popper.js` to use the `position: fixed` strategy to position the popover. */
  positionFixed: PropTypes.bool,
  /** A wrapping element for an actual content that will be used for positioning. */
  popper: customPropTypes.itemShorthand,
  /** An array containing custom settings for the Popper.js modifiers. */
  popperModifiers: PropTypes.array,
  /** A popup can have dependencies which update will schedule a position update. */
  popperDependencies: PropTypes.array,
  /** Popup size. */
  size: PropTypes.oneOf(_.without(SUI.SIZES, 'medium', 'big', 'massive')),
  /** Custom Popup style. */
  style: PropTypes.object,
  /** Element to be rendered in-place where the popup is defined. */
  trigger: PropTypes.node,
  /** Popup width. */
  wide: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['very'])])
};
Popup.Content = PopupContent;
Popup.Header = PopupHeader;
export default Popup;
//# sourceMappingURL=Popup.js.map